/**
 * Offline Initializer Component
 *
 * Handles initialization of offline data loading when user is authenticated.
 * This component should be placed inside authenticated routes to trigger
 * initial data loading for offline-first functionality.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/unified-permissions/useAuth';
import { useOfflineOperations, useDataAvailability } from '@/contexts/offline';
import { LoadingState } from '@/components/ui/loading-state';
import { Button } from '@/components/ui/button';

interface OfflineInitializerProps {
  children: React.ReactNode;
  showLoadingScreen?: boolean;
  maxLoadingTime?: number; // Maximum time to show loading screen in ms
}

export function OfflineInitializer({
  children,
  showLoadingScreen = false, // Changed default to false to prevent blocking
  maxLoadingTime = 5000 // 5 seconds default
}: OfflineInitializerProps) {
  const { user } = useAuth();
  const { initializeOfflineMode } = useOfflineOperations();
  const { hasLocalData, isDataStale } = useDataAvailability();

  // Prevent unused variable warnings
  void hasLocalData;
  void isDataStale;
  const [isInitializing, setIsInitializing] = useState(false);
  const [initializationComplete, setInitializationComplete] = useState(false);
  const [forceComplete, setForceComplete] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      // Only initialize if user is authenticated
      if (!user) {
        setInitializationComplete(true);
        return;
      }

      // Skip if already initializing or completed
      if (isInitializing || initializationComplete) {
        return;
      }

      // Always perform initial loading when user first logs in and we're online
      // This ensures all essential data is loaded for offline functionality
      if (!navigator.onLine) {


        // Even when offline, check if essential logos are cached
        // If not, we'll need to wait for online mode to cache them
        try {
          const { logoAssetManager } = await import('@/services/offline/assets/LogoAssetManager');
          const mobileLogoUrl = '/lovable-uploads/b9e6fa8f-f749-4515-b67c-d28715d804c5.png';
          const sidebarLogoUrl = '/lovable-uploads/309c4bff-08a2-44a3-9eb8-307bc935b2a8.png';

          const mobileLogoCached = await logoAssetManager.isLogoCached(mobileLogoUrl);
          const sidebarLogoCached = await logoAssetManager.isLogoCached(sidebarLogoUrl);

          if (mobileLogoCached && sidebarLogoCached) {
            console.log('✅ Essential logos are cached - offline mode ready');
          } else {
            console.log('⚠️ Some logos not cached - limited offline functionality');
          }
        } catch (error) {
          console.warn('⚠️ Error checking logo cache status:', error);
        }

        setInitializationComplete(true);
        return;
      }

      try {
        setIsInitializing(true);


        // Add timeout to prevent infinite loading
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Initialization timeout')), 15000)
        );

        const initPromise = initializeOfflineMode();

        const success = await Promise.race([initPromise, timeoutPromise]);

        if (success) {
          // Initialization completed successfully
        } else {
          console.warn('⚠️ Offline data initialization completed with warnings');
        }

        setInitializationComplete(true);
      } catch (error) {
        console.error('❌ Failed to initialize offline data:', error);
        // Still mark as complete to avoid blocking the app
        setInitializationComplete(true);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Trigger when user logs in/out

  // Force complete initialization after maxLoadingTime to prevent infinite loading
  useEffect(() => {
    if (user && isInitializing && !initializationComplete) {
      const forceCompleteTimer = setTimeout(() => {
        console.warn(`⚠️ Forcing offline initialization to complete after ${maxLoadingTime}ms timeout`);
        setForceComplete(true);
        setInitializationComplete(true);
        setIsInitializing(false);
      }, maxLoadingTime);

      return () => clearTimeout(forceCompleteTimer);
    }
  }, [user, isInitializing, initializationComplete, maxLoadingTime]);

  // Show loading screen during initialization if enabled
  if (showLoadingScreen && user && isInitializing && !initializationComplete && !forceComplete) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center z-50">
        <LoadingState
          title="Preparing your data"
          description="Setting up offline functionality and loading your content..."
          fullScreen={false}
        />
        <div className="mt-8">
          <Button
            variant="outline"
            onClick={() => {

              setForceComplete(true);
              setInitializationComplete(true);
              setIsInitializing(false);
            }}
          >
            Skip and Continue
          </Button>
        </div>
      </div>
    );
  }

  // Always render children once initialization is complete or if no user
  return <>{children}</>;
}


